import { useState, useMemo, useRef } from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts'
import { useFinance } from '../../context/FinanceContext'
import { useSubscription } from '../../context/SubscriptionContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency } from '../../lib/formatters'
import { calculateSocialSecurity } from '../../lib/calculations/socialSecurity'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import { trackExportReport } from '../../lib/analytics'
import { addChartToPDF } from '../../lib/pdfChartCapture'

function InputField({ label, value, onChange, prefix, suffix, hint, min, max, step, children }) {
  if (children) {
    return (
      <div>
        <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
        {children}
        {hint && <p className="text-xs text-faint mt-1">{hint}</p>}
      </div>
    )
  }
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-faint text-sm">{prefix}</span>}
        <input
          type="number"
          value={value}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          min={min}
          max={max}
          step={step}
          className={`w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:border-primary transition ${prefix ? 'pl-7' : 'pl-3'} ${suffix ? 'pr-12' : 'pr-3'}`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-faint text-xs">{suffix}</span>}
      </div>
      {hint && <p className="text-xs text-faint mt-1">{hint}</p>}
    </div>
  )
}

function ResultCard({ label, value, color, highlight, sub }) {
  return (
    <GlassCard className={`!p-4 ${highlight ? 'ring-1' : ''}`} style={highlight ? { borderColor: color + '44' } : {}}>
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className={`font-bold ${highlight ? 'text-xl' : 'text-lg'}`} style={{ color }}>{value}</p>
      {sub && <p className="text-xs text-faint mt-0.5">{sub}</p>}
    </GlassCard>
  )
}

export default function SocialSecurity() {
  const { state } = useFinance()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)
  const chartRef = useRef(null)

  const [currentAge, setCurrentAge] = useState(40)
  const [birthYear, setBirthYear] = useState(1986)
  const [estimatedFRABenefit, setEstimatedFRABenefit] = useState(2500)

  const result = useMemo(() => calculateSocialSecurity({
    currentAge,
    birthYear,
    estimatedFRABenefit,
  }), [currentAge, birthYear, estimatedFRABenefit])

  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 14

    // --- Helper: check if we need a new page ---
    const ensureSpace = (needed, currentY) => {
      if (currentY + needed > pageHeight - margin) {
        doc.addPage()
        return margin + 4
      }
      return currentY
    }

    // --- Title & date ---
    doc.setFillColor(255, 255, 255)
    doc.rect(0, 0, pageWidth, pageHeight, 'F')

    doc.setFontSize(20)
    doc.setTextColor(30, 30, 30)
    doc.text('Social Security Estimator', margin, 22)
    doc.setFontSize(10)
    doc.setTextColor(128, 128, 128)
    doc.text(`Generated by Liquid Finance  \u2022  ${new Date().toLocaleDateString()}`, margin, 30)

    let y = 38

    // --- Section 1: Input Parameters ---
    doc.setFontSize(13)
    doc.setTextColor(50, 50, 50)
    doc.text('Input Parameters', margin, y)
    y += 4

    autoTable(doc, {
      startY: y,
      head: [['Parameter', 'Value']],
      body: [
        ['Current Age', `${currentAge} years`],
        ['Birth Year', `${birthYear}`],
        ['Estimated Monthly Benefit at FRA', `${formatCurrency(estimatedFRABenefit)}/month`],
        ['Full Retirement Age (FRA)', result.fraDisplay],
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 10

    // --- Section 2: Key Results (mirrors 4 result cards) ---
    y = ensureSpace(30, y)
    doc.setFontSize(13)
    doc.setTextColor(50, 50, 50)
    doc.text('Key Results', margin, y)
    y += 4

    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value', 'Note']],
      body: [
        ['At Age 62', `${formatCurrency(result.benefitAt62)}/mo`, `${result.reductionAt62}% reduction`],
        ['At FRA', `${formatCurrency(result.benefitAtFRA)}/mo`, 'Full benefit'],
        ['At Age 70', `${formatCurrency(result.benefitAt70)}/mo`, `${result.increaseAt70}% increase`],
        ['Break-even Age', `Age ${result.breakEvenVs62}`, '70 overtakes 62'],
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
      columnStyles: { 2: { textColor: [120, 120, 120], fontStyle: 'italic' } },
      didParseCell: (data) => {
        // Highlight the FRA row
        if (data.section === 'body' && data.row.index === 1) {
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.fillColor = [219, 234, 254]
        }
      },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 10

    // --- Section 3: Annual Benefits Comparison Table ---
    y = ensureSpace(40, y)
    doc.setFontSize(13)
    doc.setTextColor(50, 50, 50)
    doc.text('Annual Benefits Comparison', margin, y)
    y += 4

    autoTable(doc, {
      startY: y,
      head: [['Claiming Age', 'Monthly', 'Annual', 'vs FRA']],
      body: [
        ['Age 62 (earliest)', formatCurrency(result.benefitAt62), formatCurrency(result.annualAt62), `-${result.reductionAt62}%`],
        [`FRA (${result.fraDisplay})`, formatCurrency(result.benefitAtFRA), formatCurrency(result.annualAtFRA), 'Baseline'],
        ['Age 70 (max)', formatCurrency(result.benefitAt70), formatCurrency(result.annualAt70), `+${result.increaseAt70}%`],
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
      didParseCell: (data) => {
        // Highlight the FRA row
        if (data.section === 'body' && data.row.index === 1) {
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.fillColor = [219, 234, 254]
        }
        // Color the vs FRA column
        if (data.section === 'body' && data.column.index === 3) {
          if (data.row.index === 0) data.cell.styles.textColor = [220, 38, 38]
          if (data.row.index === 2) data.cell.styles.textColor = [22, 163, 74]
        }
      },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 10

    // --- Section 4: Cumulative Benefits Chart ---
    await addChartToPDF(doc, chartRef, { title: 'Cumulative Benefits Over Time' })

    // --- Section 5: Cumulative Benefits Table (every 5 years + break-even) ---
    doc.addPage()
    let tableY = margin + 4

    doc.setFontSize(13)
    doc.setTextColor(50, 50, 50)
    doc.text('Cumulative Benefits by Age', margin, tableY)
    tableY += 4

    const cumulativeRows = result.cumulativeData
      .filter(d => d.age % 5 === 0 || d.age === result.breakEvenVs62 || d.age === result.breakEvenFRAVs62)
      .map(d => [
        `Age ${d.age}`,
        formatCurrency(d.at62),
        formatCurrency(d.atFRA),
        formatCurrency(d.at70),
      ])

    autoTable(doc, {
      startY: tableY,
      head: [['Age', 'Claim at 62', 'Claim at FRA', 'Claim at 70']],
      body: cumulativeRows,
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
      didParseCell: (data) => {
        // Highlight break-even rows
        if (data.section === 'body') {
          const cellText = data.cell.raw || ''
          if (cellText === `Age ${result.breakEvenVs62}` || cellText === `Age ${result.breakEvenFRAVs62}`) {
            data.cell.styles.fontStyle = 'bold'
            data.cell.styles.fillColor = [254, 243, 199]
          }
        }
      },
      margin: { left: margin, right: margin },
    })
    tableY = doc.lastAutoTable.finalY + 10

    // --- Section 6: Claiming Strategy Insight ---
    tableY = ensureSpace(30, tableY)
    doc.setFontSize(13)
    doc.setTextColor(50, 50, 50)
    doc.text('Claiming Strategy Insight', margin, tableY)
    tableY += 6
    doc.setFontSize(9)
    doc.setTextColor(80, 80, 80)

    let insightText = ''
    if (result.breakEvenVs62 > 0) {
      const at85_70 = result.cumulativeData.find(d => d.age === 85)?.at70 || 0
      const at85_62 = result.cumulativeData.find(d => d.age === 85)?.at62 || 0
      const diff85 = at85_70 - at85_62
      insightText = `If you live past age ${result.breakEvenVs62}, waiting until 70 to claim will give you more total lifetime benefits than claiming at 62. By age 85, claiming at 70 provides ${formatCurrency(diff85)} more in cumulative benefits. Consider your health, other income sources, and whether you need the money earlier.`
    } else {
      insightText = 'Enter your estimated FRA benefit from your SSA statement to see a personalized claiming strategy analysis.'
    }
    const insightLines = doc.splitTextToSize(insightText, pageWidth - margin * 2)
    doc.text(insightLines, margin, tableY)
    tableY += insightLines.length * 4.5 + 8

    // --- Section 7: Spousal Benefit ---
    tableY = ensureSpace(25, tableY)
    doc.setFontSize(13)
    doc.setTextColor(50, 50, 50)
    doc.text('Spousal Benefit', margin, tableY)
    tableY += 4

    autoTable(doc, {
      startY: tableY,
      head: [['Detail', 'Value']],
      body: [
        ['Spousal Benefit (50% of FRA)', `${formatCurrency(result.spousalBenefit)}/mo`],
        ['Spousal Benefit (Annual)', `${formatCurrency(result.spousalBenefit * 12)}/yr`],
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
      margin: { left: margin, right: margin },
    })
    tableY = doc.lastAutoTable.finalY + 6

    doc.setFontSize(8)
    doc.setTextColor(120, 120, 120)
    const spousalNote = 'A spouse can receive up to 50% of the higher earner\'s FRA benefit.'
    doc.text(spousalNote, margin, tableY + 4)
    tableY += 12

    // --- Section 8: Disclaimer ---
    tableY = ensureSpace(30, tableY)
    doc.setFontSize(13)
    doc.setTextColor(50, 50, 50)
    doc.text('Disclaimer', margin, tableY)
    tableY += 6
    doc.setFontSize(8)
    doc.setTextColor(120, 120, 120)
    const disclaimerText = 'This is a simplified estimator. Actual Social Security benefits depend on your 35 highest-earning years, COLA adjustments, earnings record, and other factors. Log in to ssa.gov for your personalized estimate. This calculator does not account for inflation or cost-of-living adjustments.'
    const disclaimerLines = doc.splitTextToSize(disclaimerText, pageWidth - margin * 2)
    doc.text(disclaimerLines, margin, tableY)

    doc.save(`social-security-${Date.now()}.pdf`)
  }

  return (
    <div className="space-y-6">
      {/* Inputs */}
      <GlassCard>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="text-lg font-semibold text-heading">Social Security Estimator</h3>
          <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
            Export PDF{!isPro ? ' (Pro)' : ''}
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <InputField label="Current Age" value={currentAge} onChange={setCurrentAge} suffix="years" min={18} max={70} />
          <InputField label="Birth Year" value={birthYear} onChange={setBirthYear} min={1940} max={2005} />
          <InputField label="Estimated Monthly Benefit at FRA" value={estimatedFRABenefit} onChange={setEstimatedFRABenefit} prefix="$" suffix="/month" hint="From your SSA statement" />
        </div>
        <p className="text-xs text-faint mt-3">
          Your Full Retirement Age (FRA): <strong className="text-heading">{result.fraDisplay}</strong>
        </p>
      </GlassCard>

      {/* Result Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ResultCard
          label="At Age 62"
          value={`${formatCurrency(result.benefitAt62)}/mo`}
          color={colors.negative}
          sub={`${result.reductionAt62}% reduction`}
        />
        <ResultCard
          label="At FRA"
          value={`${formatCurrency(result.benefitAtFRA)}/mo`}
          color={colors.primary}
          highlight
          sub="Full benefit"
        />
        <ResultCard
          label="At Age 70"
          value={`${formatCurrency(result.benefitAt70)}/mo`}
          color={colors.positive}
          sub={`${result.increaseAt70}% increase`}
        />
        <ResultCard
          label="Break-even Age"
          value={`Age ${result.breakEvenVs62}`}
          color={colors.warning}
          sub="70 overtakes 62"
        />
      </div>

      {/* Annual Benefits Comparison */}
      <GlassCard>
        <h3 className="text-sm font-semibold text-body mb-4">Annual Benefits Comparison</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-subtle">
                <th className="text-left py-2 text-muted font-medium">Claiming Age</th>
                <th className="text-right py-2 text-muted font-medium">Monthly</th>
                <th className="text-right py-2 text-muted font-medium">Annual</th>
                <th className="text-right py-2 text-muted font-medium">vs FRA</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-subtle">
                <td className="py-2 text-body">Age 62 (earliest)</td>
                <td className="py-2 text-right text-heading">{formatCurrency(result.benefitAt62)}</td>
                <td className="py-2 text-right text-heading">{formatCurrency(result.annualAt62)}</td>
                <td className="py-2 text-right text-negative">-{result.reductionAt62}%</td>
              </tr>
              <tr className="border-b border-subtle bg-primary-soft">
                <td className="py-2 text-body font-medium">FRA ({result.fraDisplay})</td>
                <td className="py-2 text-right text-heading font-medium">{formatCurrency(result.benefitAtFRA)}</td>
                <td className="py-2 text-right text-heading font-medium">{formatCurrency(result.annualAtFRA)}</td>
                <td className="py-2 text-right text-muted">Baseline</td>
              </tr>
              <tr className="border-b border-subtle">
                <td className="py-2 text-body">Age 70 (max)</td>
                <td className="py-2 text-right text-heading">{formatCurrency(result.benefitAt70)}</td>
                <td className="py-2 text-right text-heading">{formatCurrency(result.annualAt70)}</td>
                <td className="py-2 text-right text-positive">+{result.increaseAt70}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </GlassCard>

      {/* Cumulative Benefits Chart */}
      <GlassCard>
        <h3 className="text-sm font-semibold text-body mb-4">Cumulative Benefits Over Time</h3>
        <div ref={chartRef} className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={result.cumulativeData}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} />
              <XAxis dataKey="age" stroke={colors.chartTick} tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} stroke={colors.chartTick} tick={{ fontSize: 11 }} />
              <Tooltip
                content={({ payload, label }) => {
                  if (!payload?.[0]) return null
                  return (
                    <div className="glass px-3 py-2 text-xs rounded-lg">
                      <p className="text-heading font-medium mb-1">Age {label}</p>
                      {payload.map((p, i) => (
                        <p key={i} style={{ color: p.color }}>{p.name}: {formatCurrency(p.value)}</p>
                      ))}
                    </div>
                  )
                }}
              />
              <Legend />
              {result.breakEvenVs62 > 0 && (
                <ReferenceLine x={result.breakEvenVs62} stroke={colors.warning} strokeDasharray="5 5" label={{ value: 'Break-even', position: 'top', fontSize: 10, fill: colors.warning }} />
              )}
              <Line type="monotone" dataKey="at62" name="Claim at 62" stroke={colors.negative} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="atFRA" name="Claim at FRA" stroke={colors.primary} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="at70" name="Claim at 70" stroke={colors.positive} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>

      {/* Recommendation */}
      <GlassCard className="!p-4" style={{ borderLeft: `3px solid ${colors.primary}` }}>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${colors.primary}22`, color: colors.primary }}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-heading mb-1">Claiming Strategy Insight</p>
            <p className="text-xs text-muted leading-relaxed">
              {result.breakEvenVs62 > 0 ? (
                <>
                  If you live past age <strong className="text-heading">{result.breakEvenVs62}</strong>, waiting until 70 to claim
                  will give you more total lifetime benefits than claiming at 62.
                  By age 85, claiming at 70 provides <strong className="text-heading">
                    {formatCurrency((result.cumulativeData.find(d => d.age === 85)?.at70 || 0) - (result.cumulativeData.find(d => d.age === 85)?.at62 || 0))}
                  </strong> more in cumulative benefits.
                  {' '}Consider your health, other income sources, and whether you need the money earlier.
                </>
              ) : (
                'Enter your estimated FRA benefit from your SSA statement to see a personalized claiming strategy analysis.'
              )}
            </p>
          </div>
        </div>
      </GlassCard>

      {/* Spousal Benefit */}
      <GlassCard>
        <h3 className="text-sm font-semibold text-body mb-3">Spousal Benefit</h3>
        <p className="text-xs text-muted mb-2">A spouse can receive up to 50% of the higher earner's FRA benefit.</p>
        <div className="flex items-center gap-2 p-3 rounded-xl bg-subtle">
          <span className="text-sm text-body">Spousal benefit:</span>
          <span className="text-sm font-semibold text-heading">{formatCurrency(result.spousalBenefit)}/mo</span>
          <span className="text-xs text-faint">({formatCurrency(result.spousalBenefit * 12)}/yr)</span>
        </div>
      </GlassCard>

      {/* Disclaimer */}
      <div className="p-3 rounded-xl border border-warning/30 bg-warning/5">
        <div className="flex gap-2">
          <svg className="w-4 h-4 text-warning shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-xs text-muted leading-relaxed">
            <strong className="text-body">Disclaimer:</strong> This is a simplified estimator. Actual Social Security benefits depend on your 35 highest-earning years,
            COLA adjustments, earnings record, and other factors. Log in to <strong>ssa.gov</strong> for your personalized estimate.
            This calculator does not account for inflation or cost-of-living adjustments.
          </p>
        </div>
      </div>
    </div>
  )
}
